/**
 * Current-page indicator of the topbar (backlog #9, D28: indigo = "you are here").
 *
 * One resolver for the desktop bar, its dropdowns and the mobile menu, so they always agree.
 * A page belongs to a section; a topbar link points at a section too. The link is current when
 * both sections match:
 * - aria-current="page" when the link points exactly at the open page (same route, params and
 *   query, no in-page hash);
 * - aria-current="true" when the open page is inside the link's section (a chat under «Заказы»,
 *   the edit wizard under «Мои задачи и услуги», /account/* sub-pages under «Настройки аккаунта»,
 *   a filtered service search under «Все услуги»).
 */
import { matchPathname } from '../../../util/routes';
import { getSearchType } from '../../SearchPage/SearchPage.shared';

// Modal state params of the topbar itself; they don't change the page.
const IGNORED_QUERY_PARAMS = ['mobilemenu', 'mobilesearch'];

const SEARCH_ROUTES = ['SearchPage', 'SearchPageWithListingType'];
const INBOX_ROUTES = ['InboxPage', 'OrderDetailsPage', 'SaleDetailsPage'];
const ACCOUNT_SETTINGS_ROUTES = [
  'AccountSettingsPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'StripePayoutOnboardingPage',
  'PaymentMethodsPage',
  'ManageAccountPage',
];
const EDIT_LISTING_ROUTES = ['EditListingPage', 'EditListingStripeOnboardingPage'];

const queryToObject = search => {
  const result = {};
  new URLSearchParams(search || '').forEach((value, key) => {
    if (!IGNORED_QUERY_PARAMS.includes(key)) {
      result[key] = value;
    }
  });
  return result;
};

const isSameObject = (a = {}, b = {}) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  return keysA.length === keysB.length && keysA.every(k => `${a[k]}` === `${b[k]}`);
};

/**
 * Topbar section of a route: the key shared by a page and the topbar link of its section.
 *
 * @param {string} name route name
 * @param {Object?} params route params
 * @param {Object} query parsed query (see queryToObject)
 * @returns {string|null} section key, or null when no topbar item stands for it
 */
export const getSection = (name, params = {}, query = {}) => {
  if (!name) {
    return null;
  }
  if (SEARCH_ROUTES.includes(name)) {
    // Service search → «Все услуги», task search → «Найти задачу», a mixed search → nothing.
    const searchType = getSearchType(params.listingType, query);
    return searchType === 'all' ? null : `search:${searchType}`;
  }
  if (INBOX_ROUTES.includes(name)) {
    return 'inbox';
  }
  if (name === 'ManageListingsPage') {
    return 'manageListings';
  }
  if (name === 'NewListingPage') {
    // «Создать услугу» is an action, not a place (it redirects into the new-listing wizard).
    return null;
  }
  if (EDIT_LISTING_ROUTES.includes(name)) {
    // Editing an existing listing is inside «Мои …»; the new-listing wizard is an action, not a place.
    return params.type === 'edit' ? 'manageListings' : null;
  }
  if (ACCOUNT_SETTINGS_ROUTES.includes(name)) {
    return 'accountSettings';
  }
  if (name === 'SignupPage' || name === 'SignupForUserTypePage') {
    return 'signup';
  }
  if (name === 'CMSPage') {
    return `CMSPage:${params.pageId}`;
  }
  // Every other page is its own section (e.g. AboutPage, ProfileSettingsPage, LoginPage).
  return name;
};

/**
 * The open page as the topbar sees it.
 *
 * @param {Object} location { pathname, search }
 * @param {Array<Object>} routeConfiguration
 * @returns {Object} { name, params, query, section }
 */
export const getCurrentNav = (location, routeConfiguration) => {
  const found = matchPathname(location?.pathname || '/', routeConfiguration)[0];
  const name = found?.route?.name || null;
  const params = found?.params || {};
  const query = queryToObject(location?.search);
  return { name, params, query, section: getSection(name, params, query) };
};

/**
 * aria-current of a topbar link.
 *
 * @param {Object} target the link: { name, params, to: { search, hash } } (NamedLink props or a
 * resolved hosted link route)
 * @param {Object?} currentNav from getCurrentNav
 * @returns {'page'|'true'|undefined}
 */
export const getAriaCurrent = (target, currentNav) => {
  if (!target?.name || !currentNav?.section) {
    return undefined;
  }
  const { name, params = {}, to = {} } = target;
  // An in-page anchor (e.g. «Все категории» → /#all-categories) is never a place of its own.
  if (to.hash) {
    return undefined;
  }
  const query = queryToObject(to.search);
  if (getSection(name, params, query) !== currentNav.section) {
    return undefined;
  }
  const isExact =
    name === currentNav.name &&
    isSameObject(params, currentNav.params) &&
    isSameObject(query, currentNav.query);
  return isExact ? 'page' : 'true';
};

/**
 * aria-current of a resolved hosted link ({ type, href, route }); external links are never current.
 */
export const getLinkAriaCurrent = (linkConfig, currentNav) =>
  linkConfig?.type === 'internal' && linkConfig.route
    ? getAriaCurrent(linkConfig.route, currentNav)
    : undefined;
